<script setup lang="ts">
import { useQuery } from '@tanstack/vue-query';
import { ref, computed } from 'vue';
import {
  patientServiceCategoriesApi,
  type PatientServiceCategory,
} from '@/api/patient-service-categories';
import { usePermissions } from '@/composables/usePermissions';
import { useResponsiveTable } from '@/composables/useResponsiveTable';
import ListToolbar from '@/components/list-toolbar.vue';
import ResponsiveDialog from '@/components/responsive-dialog.vue';
import PatientServiceCategoryForm from './patient-service-category-form.vue';
import PatientServiceCategoryDiscardForm from './patient-service-category-discard-form.vue';
import PatientServiceCategoryUndiscardForm from './patient-service-category-undiscard-form.vue';

const PERMISSION_RESOURCE = 'patient_service_categories';

const { hasPermission } = usePermissions();
const { tableProps } = useResponsiveTable();

const selectedCategory = ref<PatientServiceCategory | null>(null);
const showForm = ref(false);
const discardCategory = ref<PatientServiceCategory | null>(null);
const undiscardCategory = ref<PatientServiceCategory | null>(null);
const scope = ref<'active' | 'discarded'>('active');
const hasActions = hasPermission.value(PERMISSION_RESOURCE, 'update') ||
  hasPermission.value(PERMISSION_RESOURCE, 'destroy');

const {
  data: activeCategories,
  refetch: refetchActiveCategories,
  isPending: isLoadingActiveCategories,
} = useQuery({
  queryKey: ['patientServiceCategories', 'active'],
  queryFn: () => patientServiceCategoriesApi.index('active'),
  initialData: [],
});

const {
  data: discardedCategories,
  refetch: refetchDiscardedCategories,
  isPending: isLoadingDiscardedCategories,
} = useQuery({
  queryKey: ['patientServiceCategories', 'discarded'],
  queryFn: () => patientServiceCategoriesApi.index('discarded'),
  initialData: [],
});

const categories = computed(() =>
  (scope.value === 'active' ? activeCategories.value : discardedCategories.value));

const isLoading = computed(() =>
  isLoadingActiveCategories.value || isLoadingDiscardedCategories.value);

const actionSuccess = ref(false);

function keptSubcategoryNames(category: PatientServiceCategory) {
  return category.patientServiceSubcategories
    .filter((subcategory) => !subcategory.discarded)
    .map((subcategory) => subcategory.name);
}

function openCreateForm() {
  selectedCategory.value = null;
  showForm.value = true;
}

function openEditForm(category: PatientServiceCategory) {
  selectedCategory.value = category;
  showForm.value = true;
}

function handleDiscardAndUndiscardSuccess() {
  refetchActiveCategories();
  refetchDiscardedCategories();
  actionSuccess.value = true;
}

function handleFormSuccess() {
  actionSuccess.value = true;
}

function handleFormClose() {
  showForm.value = false;
  selectedCategory.value = null;
}

const headers: { title: string, key: string, sortable?: boolean }[] = [
  { title: 'Nombre', key: 'name' },
  { title: 'Prioridad', key: 'priority' },
  { title: 'Visitas prioritarias', key: 'prioritizationEnabled' },
  { title: 'Subcategorías', key: 'subcategories', sortable: false },
  { title: 'Creada', key: 'humanCreatedAt' },
];
if (hasActions) {
  headers.push({ title: 'Acciones', key: 'actions', sortable: false });
}
</script>
<template>
  <v-card>
    <v-data-table
      v-bind="tableProps"
      :headers="headers"
      :header-props="{ class: 'font-weight-bold' }"
      :items="categories"
      :loading="isLoading"
      loading-text="Cargando categorías de pacientes..."
    >
      <template #top>
        <list-toolbar title="Categorías de Pacientes">
          <template #actions>
            <v-btn
              v-if="scope === 'active' && hasPermission(PERMISSION_RESOURCE, 'create')"
              class="text-white border bg-primary"
              @click="openCreateForm"
            >
              <v-icon
                start
                icon="mdi-plus"
              />
              Agregar Categoría
            </v-btn>
          </template>
          <template #tabs>
            <v-tabs
              v-model="scope"
              color="primary"
            >
              <v-tab
                value="active"
                class="rounded-pill text-body-1"
              >
                Activas
              </v-tab>
              <v-tab
                value="discarded"
                class="rounded-pill text-body-1"
              >
                Archivadas
              </v-tab>
            </v-tabs>
          </template>
        </list-toolbar>
      </template>

      <template #[`item.priority`]="{ item }">
        <v-chip
          color="primary"
          size="small"
        >
          {{ item.priority }}
        </v-chip>
      </template>

      <template #[`item.prioritizationEnabled`]="{ item }">
        <v-icon
          v-if="item.prioritizationEnabled"
          icon="mdi-star"
          color="warning"
          size="small"
        />
        <span
          v-else
          class="text-body-2 text-medium-emphasis"
        >
          —
        </span>
      </template>

      <template #[`item.subcategories`]="{ item }">
        <div class="d-flex flex-wrap ga-1 py-1">
          <v-chip
            v-for="subcategoryName in keptSubcategoryNames(item)"
            :key="subcategoryName"
            size="x-small"
            variant="outlined"
          >
            {{ subcategoryName }}
          </v-chip>
          <span
            v-if="keptSubcategoryNames(item).length === 0"
            class="text-body-2 text-medium-emphasis"
          >
            —
          </span>
        </div>
      </template>

      <template #[`item.actions`]="{ item }">
        <div
          v-if="scope === 'active' && hasActions"
          class="d-flex"
        >
          <v-btn
            v-if="hasPermission(PERMISSION_RESOURCE, 'update')"
            icon="mdi-pencil"
            size="small"
            variant="text"
            @click="openEditForm(item)"
          />
          <v-btn
            v-if="hasPermission(PERMISSION_RESOURCE, 'destroy')"
            icon="mdi-archive"
            size="small"
            variant="text"
            color="warning"
            @click="discardCategory = item"
          />
        </div>
        <v-btn
          v-else-if="scope === 'discarded' && hasPermission(PERMISSION_RESOURCE, 'update')"
          icon="mdi-package-up"
          size="small"
          variant="text"
          color="success"
          @click="undiscardCategory = item"
        />
      </template>
    </v-data-table>

    <responsive-dialog
      v-model="showForm"
      max-width="600"
      scrollable
      @close="handleFormClose"
    >
      <patient-service-category-form
        :patient-service-category="selectedCategory"
        @close="handleFormClose"
        @success="handleFormSuccess"
      />
    </responsive-dialog>

    <responsive-dialog
      :model-value="!!discardCategory"
      max-width="600"
      scrollable
      :mobile-fullscreen="false"
      @update:model-value="discardCategory = null"
    >
      <patient-service-category-discard-form
        v-if="discardCategory"
        :patient-service-category="discardCategory"
        @close="discardCategory = null"
        @success="handleDiscardAndUndiscardSuccess"
      />
    </responsive-dialog>

    <responsive-dialog
      :model-value="!!undiscardCategory"
      max-width="600"
      scrollable
      :mobile-fullscreen="false"
      @update:model-value="undiscardCategory = null"
    >
      <patient-service-category-undiscard-form
        v-if="undiscardCategory"
        :patient-service-category="undiscardCategory"
        @close="undiscardCategory = null"
        @success="handleDiscardAndUndiscardSuccess"
      />
    </responsive-dialog>
  </v-card>

  <v-snackbar
    v-model="actionSuccess"
    :timeout="2000"
    color="success"
    rounded="pill"
  >
    <v-icon
      icon="mdi-check-circle"
      class="mr-4"
    />
    Cambios guardados exitosamente!
  </v-snackbar>
</template>
